import { PageAction, PageActions } from '@e3dc-react/shell/State/App/AppModel'
import { AppBar, Box, Divider, Grid, IconButton, Slide, Stack, Toolbar, Typography, useScrollTrigger } from '@mui/material'
import React, { useCallback } from 'react'
import { useUser } from 'src/Hooks/useUser'
import { makeStyles } from 'tss-react/mui'
import { pageActionIcons } from '../Framework/View/pageActionIcons'
import MenuIcon from '@mui/icons-material/Menu'
import TabsBar, { TabsBarTab } from '@e3dc-react/shell/Elements/Tabs/TabsBar'
import { useHeaderBreadcrumbs, useHeaderHeight } from '@e3dc-react/shell/Libs/Header'
import { Breadcrumbs } from '@e3dc-react/shell/Elements/Header'
import { ShopBasket } from 'src/Libs/shop'
import { NotificationMenuBadge } from 'src/Libs/notifications/components'
import { mainAppBarId } from '../../../../../packages/shell/src/Elements/Header/HeaderBar'
import { isFlowTheme } from 'src/Libs/utils'

const useStyles = makeStyles()(theme => ({
  appBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    zIndex: theme.zIndex.appBar,
    backgroundColor: theme.palette.background.header,
    color: theme.palette.text.header,
    background: 'transparent',
    boxShadow: 'none',
  },
  topBar: {
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    padding: 0,
    paddingTop: theme.spacing(1),
    color: theme.palette.text.header,
    background: theme.palette.background.header,
    alignItems: 'baseline',
    boxShadow: theme.effectStyles.shadowMain,
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
  },
  mobileFont: {
    [theme.breakpoints.down('md')]: {
      textOverflow: 'ellipsis',
      fontSize: 22,
      fontWeight: 400,
      paddingLeft: 4,
      overflow: 'hidden',
      flexGrow: 1,
    },
  },
  divider: {
    borderColor: theme.palette.shades.shade03,
    height: '30px',
    width: '1px',
  },
  tab: {
    maxWidth: '100%',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
    },
  },
  breadcrumbs: {
    paddingTop: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      padding: `0 0 ${theme.spacing(2)} ${theme.spacing(2)}`,
    },
  },
}))

interface Props {
  title: string
  handleLeftAction: (type: PageAction) => void
  pageActions: PageActions | []
  hasMenu?: boolean
  handleDrawerToggle?: () => void
  tabs: TabsBarTab[]
  activeTab: number
  customContent: React.ReactNode
  showTitle?: boolean
}

/**
 * Displays the header bar for mobile view (down md breakpoint)
 */
const MobileHeaderBar: React.FC<Props> = ({
  title,
  handleLeftAction,
  tabs,
  activeTab,
  pageActions,
  handleDrawerToggle,
  customContent,
  showTitle = true,
  hasMenu = false,
}) => {
  const { classes } = useStyles()
  const { userData } = useUser()
  const trigger = useScrollTrigger()
  const { breadcrumbs } = useHeaderBreadcrumbs()
  const { setHeight } = useHeaderHeight()

  const measuredRef = useCallback(
    (node: HTMLDivElement) => {
      if (node !== null) {
        const height = node.getBoundingClientRect().height

        /** since <Hidden> is deprecated and the display: 'none' does not disable the ref, we have to check if height is more that 0 */
        if (height > 0) setHeight(height)
      }
    },
    /* tabs and breadcrumbs are loaded afterwards to the node, so we need to react to them to get the correct height */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setHeight, tabs, breadcrumbs, customContent]
  )

  const LeftActions = pageActions
    ?.filter(action => action.position === 'left')
    .map(page => (
      <Grid item key={page.type}>
        <IconButton
          color="inherit"
          aria-label="left action"
          onClick={() => {
            if (page.onClick) {
              page.onClick()
            } else if (page.type === 'back') handleLeftAction('back')
          }}
          size="large"
        >
          {page.type !== 'custom' ? pageActionIcons[page.type] : page.icon}
        </IconButton>
      </Grid>
    ))

  const RightActions = pageActions
    ?.filter(action => action.position !== 'left')
    .map(action => (
      <Grid item key={action.type}>
        <IconButton
          color="inherit"
          aria-label="top right action"
          disabled={
            !(
              action.type !== 'add' ||
              (userData?.installerCertified && userData?.level === 'INSTALLER') ||
              userData?.level === 'E3DC_SERVICE' ||
              userData?.level === 'EXT_SERVICE_PLUS'
            )
          }
          onClick={() => action.onClick?.() ?? undefined}
          size="large"
        >
          {action.type === 'custom' ? action.icon : pageActionIcons[action.type]}
        </IconButton>
      </Grid>
    ))

  return (
    <Slide direction="down" in={!trigger}>
      <AppBar className={classes.appBar} position="sticky" ref={measuredRef}>
        <Toolbar variant="dense" className={classes.topBar} id={mainAppBarId}>
          <Stack spacing={0} alignItems="center" sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
            <Box className={classes.contentContainer}>
              <Box
                display={'flex'}
                flexDirection={'row'}
                alignItems={'center'}
                gap={1}
                sx={{ flewGrow: 1, flexShrink: 1, minWidth: 0, pl: { xs: 1, lg: 0 } }}
              >
                {hasMenu && (
                  <>
                    <IconButton
                      color="inherit"
                      aria-label="open drawer"
                      onClick={handleDrawerToggle}
                      data-qa={'button-header-menu'}
                      size="large"
                      sx={{ height: 30, width: 30 }}
                    >
                      <MenuIcon />
                    </IconButton>
                    <Divider orientation="vertical" className={classes.divider} />
                  </>
                )}
                {pageActions && LeftActions}
                {customContent}
              </Box>
              {showTitle && (
                <Typography variant="h6" className={classes.mobileFont}>
                  {title}
                </Typography>
              )}
              <Stack
                sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, flexGrow: 0, gap: 0, minWidth: 0, pr: { xs: 1, lg: 0 } }}
                direction={'row'}
              >
                {pageActions && RightActions}
                <ShopBasket />
                {!isFlowTheme && <NotificationMenuBadge />}
              </Stack>
            </Box>
          </Stack>
          {breadcrumbs && breadcrumbs.length > 0 && (
            <Box className={classes.breadcrumbs}>
              <Breadcrumbs breadcrumbs={breadcrumbs} />
            </Box>
          )}
          {tabs && tabs.length > 0 && (
            <Box className={classes.tab}>
              <TabsBar tabs={tabs} activeTab={activeTab ?? 0} />
            </Box>
          )}
        </Toolbar>
      </AppBar>
    </Slide>
  )
}

export default MobileHeaderBar
